import { getFieldDescriptor } from '@gef-ui/redux-common/selectors/fieldDescriptor';
import { getFormData, getFormDataValue } from '@gef-ui/redux-common/selectors/formData';
import { getRegisteredField, isRegisteredFieldFieldPristine } from '@gef-ui/redux-common/selectors/registeredFields';
import T from "ramda/es/T";
import __ from "ramda/es/__";
import always from "ramda/es/always";
import append from "ramda/es/append";
import compose from "ramda/es/compose";
import concat from "ramda/es/concat";
import cond from "ramda/es/cond";
import converge from "ramda/es/converge";
import curry from "ramda/es/curry";
import defaultTo from "ramda/es/defaultTo";
import equals from "ramda/es/equals";
import filter from "ramda/es/filter";
import fromPairs from "ramda/es/fromPairs";
import identity from "ramda/es/identity";
import ifElse from "ramda/es/ifElse";
import includes from "ramda/es/includes";
import isNil from "ramda/es/isNil";
import juxt from "ramda/es/juxt";
import keys from "ramda/es/keys";
import map from "ramda/es/map";
import mapObjIndexed from "ramda/es/mapObjIndexed";
import mergeRight from "ramda/es/mergeRight";
import o from "ramda/es/o";
import over from "ramda/es/over";
import pickAll from "ramda/es/pickAll";
import prop from "ramda/es/prop";
import reduce from "ramda/es/reduce";
import reject from "ramda/es/reject";
import toPairs from "ramda/es/toPairs";
import values from "ramda/es/values";
import view from "ramda/es/view";
import when from "ramda/es/when";
import zipObj from "ramda/es/zipObj";
import isFunction from "ramda-extension/es/isFunction";
import isObject from "ramda-extension/es/isObject";
import rejectNil from "ramda-extension/es/rejectNil";
import { severity, validationCombination } from '../../constants';
import asyncValidationResolver from '../../factory/asyncValidationResolver';
import { customRequiredMessagePath, fieldNameLens, filterAlways, filterError, filterInvalid, filterRequired, requiredPath, validateP, validationLens } from '../../utils';
import commonValidation from '../../validationFunctions';
const pickFieldValueProps = pickAll([
    'deepPath',
    'temp'
]);
const callWhen = (...params)=>compose((when)=>when && !when(...params), prop('when'));
const getValidationParams = ({ formName, state, fieldDescriptor })=>{
    if (fieldDescriptor) {
        return {
            fieldValue: getFormDataValue(formName)(state)(pickFieldValueProps(fieldDescriptor)),
            formData: getFormData(formName)(state),
            field: fieldDescriptor.descriptor.name,
            descriptor: fieldDescriptor.descriptor,
            state,
            formName
        };
    }
    return {
        formData: getFormData(formName)(state),
        state
    };
};
export const runValidations = ({ validations, ...other })=>{
    const validationParams = getValidationParams(other);
    const validationFn = compose(juxt, map(validateP), reject(callWhen(validationParams)))(validations);
    return validationFn(validationParams);
};
const validateGroup = curry((filterFn, { fieldDescriptor, ...other })=>{
    const validations = fieldDescriptor.descriptor.validations;
    return validations && runValidations({
        validations: filterFn(validations),
        fieldDescriptor,
        ...other
    });
});
const validateAll = validateGroup(filterAlways);
const validateRequired = validateGroup(filterRequired);
const validateError = validateGroup(filterError);
const validCombs = cond([
    [
        equals(validationCombination.ALL),
        always([
            validateAll
        ])
    ],
    [
        equals(validationCombination.R),
        always([
            validateRequired
        ])
    ],
    [
        equals(validationCombination.RE),
        always([
            validateRequired,
            validateError
        ])
    ]
]);
const mergeResults = reduce(concat, []);
const addFieldName = (fieldName)=>map((res)=>({
            ...res,
            fieldName
        }));
const postProcessResult = compose(filterInvalid, mergeResults, rejectNil);
const whenCondition = curry((formName, state, fieldDescriptor)=>{
    const formData = getFormData(formName)(state);
    return callWhen({
        step: formData.step,
        formData,
        fieldDescriptor,
        state,
        formName
    })(fieldDescriptor.descriptor);
});
const validate = curry((type, formName, state, fieldDescriptors)=>mapObjIndexed(ifElse(whenCondition(formName, state), always([]), (fieldDescriptor, fieldName)=>compose(addFieldName(fieldName), postProcessResult, juxt(validCombs(type)))({
            formName,
            state,
            fieldDescriptor
        })), fieldDescriptors));
const requiredValidation = (messageKey)=>commonValidation({
        functionName: 'isRequired',
        severity: severity.REQUIRED,
        args: {
            messageKey
        }
    });
const isRequiredByFunc = o(isFunction, requiredPath);
const solveRequiredFn = (formData, formName, state)=>(fieldDescriptor)=>{
        const fieldValue = getFormDataValue(formName)(state)(pickFieldValueProps(fieldDescriptor));
        const result = requiredPath(fieldDescriptor)({
            fieldValue,
            formData,
            formName,
            state,
            fieldDescriptor
        });
        if (result) {
            return over(validationLens, append(requiredValidation(result === true ? undefined : result)))(fieldDescriptor);
        }
        return fieldDescriptor;
    };
const appendRequiredFunctionToDescriptor = (fieldRecord)=>over(validationLens, append(requiredValidation(customRequiredMessagePath(fieldRecord))))(fieldRecord);
const addRequiredValidation = (formData, formName, state)=>map(ifElse(isRequiredByFunc, solveRequiredFn(formData, formName, state), when(requiredPath, appendRequiredFunctionToDescriptor)));
export const validateForm = (type)=>(formName, state)=>{
        const formData = getFormData(formName)(state);
        const fieldDescriptors = getFieldDescriptor(formName)(state);
        const newDescriptor = addRequiredValidation(formData, formName, state)(fieldDescriptors);
        return validate(type, formName, state, newDescriptor);
    };
const containsField = (fieldsToValid)=>compose(includes(__, fieldsToValid), view(fieldNameLens));
const expandAndConcat = (a, b)=>concat(a, defaultTo([], b.dependentFields));
const getDependentFieldName = (dependentField)=>dependentField?.fieldName || dependentField;
const expandDepFields = curry((fieldDescriptor, getRegisteredField, filteredDescriptor)=>{
    const dependentFields = reduce(expandAndConcat, [], values(filteredDescriptor));
    const onlyRegistered = reject(compose(isNil, getRegisteredField, getDependentFieldName), dependentFields);
    const dependentDescriptor = converge(zipObj, [
        map(getDependentFieldName),
        map((dependentField)=>{
            if (isObject(dependentField)) {
                return {
                    ...prop(dependentField.fieldName, fieldDescriptor),
                    ...dependentField
                };
            } else {
                return prop(dependentField, fieldDescriptor);
            }
        })
    ])(onlyRegistered);
    return mergeRight(filteredDescriptor, dependentDescriptor);
});
const runFieldValidations = curry((state, formName, isPristine, validationType, manipulationFn)=>validationType ? compose(validate(validationType, formName, state), manipulationFn(isPristine)) : T);
const rejectPristine = (isPristine)=>compose(fromPairs, reduce((acc, descriptor)=>{
        if (isPristine(descriptor[1]) && !descriptor[1].validatePristine) {
            return acc;
        }
        return [
            ...acc,
            descriptor
        ];
    }, []), toPairs);
export const validateFields = (type, typePristine)=>curry((fieldsToValidate, formName, state)=>{
        const fieldDescriptors = getFieldDescriptor(formName)(state);
        const registeredField = getRegisteredField(formName)(state);
        const pristineField = isRegisteredFieldFieldPristine(formName)(state);
        const expandedDepFields = expandDepFields(fieldDescriptors, registeredField);
        const formData = getFormData(formName)(state);
        const processedDescriptor = compose(addRequiredValidation(formData, formName, state), expandedDepFields, filter(containsField(fieldsToValidate)))(fieldDescriptors);
        const preparedValidators = runFieldValidations(state, formName, compose(pristineField, view(fieldNameLens)));
        const runner = converge(mergeRight, [
            preparedValidators(type, rejectPristine),
            preparedValidators(typePristine, filter)
        ]);
        return runner(processedDescriptor);
    });
const runValidationsAsync = ({ dispatch, fieldDescriptor, ...other })=>{
    const validations = fieldDescriptor.descriptor.asyncValidations;
    if (!validations) {
        return Promise.resolve([]);
    }
    const validationParams = getValidationParams({
        fieldDescriptor,
        ...other
    });
    const validationFn = compose(juxt, map(asyncValidationResolver(dispatch)), reject(callWhen(validationParams)))(validations);
    return Promise.all(validationFn(validationParams));
};
const createResultWithGuid = (fieldsWithGuids, fieldName)=>(msgs)=>({
            msgs,
            validationGuid: fieldsWithGuids[fieldName].validationGuid
        });
const createRejectWithGuid = (fieldsWithGuids, fieldName)=>(originalError)=>Promise.reject({
            originalError,
            validationGuid: fieldsWithGuids[fieldName].validationGuid
        });
const validateAsync = (fieldsWithGuids, formName, state, dispatch, fieldDescriptors)=>mapObjIndexed(ifElse(whenCondition(formName, state), (_fieldDescriptor, fieldName)=>Promise.resolve(createResultWithGuid(fieldsWithGuids, fieldName)([])), (fieldDescriptor, fieldName)=>runValidationsAsync({
            formName,
            state,
            fieldDescriptor,
            dispatch
        }).then(filterInvalid).then(addFieldName(fieldName)).then(createResultWithGuid(fieldsWithGuids, fieldName)).catch(createRejectWithGuid(fieldsWithGuids, fieldName))), fieldDescriptors);
export const validateAsyncFields = (validatePristine)=>curry((fieldsWithGuids, formName, state, dispatch)=>{
        const getFieldsToValidate = filter(containsField(keys(fieldsWithGuids)));
        const isPristine = o(isRegisteredFieldFieldPristine(formName)(state), view(fieldNameLens));
        const handlePristine = validatePristine ? identity : reject(isPristine);
        const processedDescriptor = compose(handlePristine, getFieldsToValidate, getFieldDescriptor(formName))(state);
        return validateAsync(fieldsWithGuids, formName, state, dispatch, processedDescriptor);
    });
